<template>
  <div class="gl-codes-edit flex flex-column flex-gap-1 align-items-start">
    <p v-if="!internalValue.length">
      {{ t('common.noParam', { param: t('glCodes.glCodes', 2) }) }}
    </p>
    <GLCodeEdit
      v-for="(glCode, index) of internalValue"
      :key="glCode.code"
      :model-value="glCode"
      :disabled="disabled"
      :gl-codes="availableGLCodeOptions"
      class="align-self-stretch"
      @remove="onRemove(index)"
      @update:model-value="onUpdate(index, $event)"
    />
    <InputError v-for="error of errors" :key="error.$uid" :error="error" />
    <AddButton
      :disabled="disabled"
      :label="t('glCodes.glCodes')"
      class="w-auto"
      @click="onAdd"
    />
  </div>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import { computed, onMounted, PropType, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useValidations } from '@rygen/rygen-ui/composables';
import { GLCode } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { useGLCodeStore } from '@/stores/GLCode';
import { useParentEntityStore } from '@/stores/ParentEntity';

import GLCodeEdit from '@/components/glCode/GLCodeEdit.vue';
import AddButton from '@/components/ui/AddButton.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';

const props = defineProps({
  modelValue: {
    type: Array as PropType<GLCode[]>,
    default: () => [],
  },
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, errors, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: GLCode[]): void;
}>();

const { availableGLCodes, loadGLCodes } = useGLCodeStore();
const { t } = useI18n();
const { owningClient } = useParentEntityStore();

const internalValue: Ref<GLCode[]> = ref(modelValue.value);
useValidations(internalValue, {});

// Filter out already used types
const availableGLCodeOptions = computed(() => {
  return (
    availableGLCodes.value.data
      ?.filter((clientType) => {
        const existingGLCode = internalValue.value.find(
          (g) => g.code && g.code === clientType.code,
        );
        return !existingGLCode;
      })
      ?.filter(notNull) ?? []
  );
});

const onAdd = () => {
  internalValue.value.push(new GLCode());
};

const onRemove = (index: number) => {
  internalValue.value.splice(index, 1);
};

const onUpdate = (index: number, glCode: GLCode) => {
  internalValue.value.splice(index, 1, glCode);
};

watch(
  modelValue,
  (newVal: GLCode[]) => {
    internalValue.value = newVal;
  },
  { immediate: true, deep: true },
);

watch(
  internalValue,
  (newVal: GLCode[]) => {
    emit('update:model-value', newVal);
  },
  { deep: true },
);

onMounted(() => {
  if (!disabled.value && owningClient.value?.id) {
    loadGLCodes();
  }
});
</script>
